import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { ChangeEvent, KeyboardEvent } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { alpha } from '@mui/material/styles';
import { tss } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';

import {
  ROOT_TYPE_OPTIONS,
  addObjectPropertyAtPath,
  buildSchemaTreeEntries,
  changeNodeType,
  countUnsupportedFields,
  deleteObjectPropertyAtPath,
  formatSchemaPath,
  getNodeAtPath,
  getParentPath,
  getPathFieldName,
  isDestructiveTypeChange,
  isRequiredAtPath,
  keyToPath,
  pathToKey,
  renameObjectPropertyAtPath,
  setNodeDescriptionAtPath,
  setStringNodeEnumAtPath,
  toggleRequiredAtPath,
  updateNodeAtPath,
  validateStructuredOutputSchemaEditorValue,
} from './schemaEditorUtils';
import {
  StructuredOutputNodeType,
  StructuredOutputSchemaNode,
  StructuredOutputSchemaPath,
} from './types';

const useStyles = tss.create(({ theme }) => ({
  root: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  editorSplit: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: 540,
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'stretch',
    },
  },
  treePaper: {
    width: '100%',
    padding: theme.spacing(1.5),
    overflowY: 'auto',
    [theme.breakpoints.up('md')]: { width: '44%' },
  },
  fieldPaper: {
    width: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: { width: '56%' },
  },
  treeRow: {
    minHeight: 36,
    display: 'grid',
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    gridTemplateColumns: '8px 1fr',
    '&:hover, &:focus-within': { backgroundColor: theme.palette.action.hover },
  },
  treeRowActive: { backgroundColor: theme.palette.primary.light },
  treeRowDepth1: { gridTemplateColumns: '24px 1fr' },
  treeRowDepth2: { gridTemplateColumns: '40px 1fr' },
  treeRowDepth3: { gridTemplateColumns: '56px 1fr' },
  treeRowDepth4: { gridTemplateColumns: '72px 1fr' },
  treeRowDepth5: { gridTemplateColumns: '88px 1fr' },
  treeRowDepth6: { gridTemplateColumns: '104px 1fr' },
  treeRowContent: {
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minWidth: 0,
  },
  treeRowMeta: {
    flexShrink: 0,
    minWidth: 0,
  },
  treeRowActions: {
    flexShrink: 0,
    marginLeft: 'auto',
  },
  treeRowActionButton: {
    padding: theme.spacing(0.375),
  },
  treeName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  typeToggleGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(0.75),
    width: '100%',
    border: 0,
    '& .MuiToggleButton-root': {
      minWidth: 0,
      margin: 0,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: `${theme.shape.borderRadius}px !important`,
      textTransform: 'none',
      minHeight: 44,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    '& .MuiToggleButtonGroup-grouped': {
      border: `1px solid ${theme.palette.divider} !important`,
      margin: 0,
    },
  },
  jsonPreview: {
    margin: 0,
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  metaLabel: {
    color: theme.palette.text.secondary,
  },
  badgeString: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.grey[700],
    height: 20,
  },
  badgeNumber: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    height: 20,
  },
  badgeInteger: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    height: 20,
  },
  badgeBoolean: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    height: 20,
  },
  badgeArray: {
    backgroundColor: alpha(theme.palette.chart[9], 0.12),
    color: theme.palette.chart[9],
    height: 20,
  },
  badgeObject: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.dark,
    height: 20,
  },
}));

type StructuredOutputSchemaEditorProps = {
  value: StructuredOutputSchemaNode;
  onChange: (value: StructuredOutputSchemaNode) => void;
  error?: string | null;
  disabled?: boolean;
  showValidation?: boolean;
};

type PendingTypeChange = {
  path: StructuredOutputSchemaPath;
  nextType: StructuredOutputNodeType;
};

const badgeClassMap: Record<
  StructuredOutputNodeType,
  keyof ReturnType<typeof useStyles>['classes']
> = {
  string: 'badgeString',
  number: 'badgeNumber',
  integer: 'badgeInteger',
  boolean: 'badgeBoolean',
  array: 'badgeArray',
  object: 'badgeObject',
};

const StructuredOutputSchemaEditor: React.FC<StructuredOutputSchemaEditorProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  showValidation = true,
}) => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const [selectedPathKey, setSelectedPathKey] = useState(pathToKey([]));
  const [fieldNameDraft, setFieldNameDraft] = useState('');
  const [enumValueDraft, setEnumValueDraft] = useState('');
  const [isEnumEditorEnabled, setIsEnumEditorEnabled] = useState(false);
  const [pendingTypeChange, setPendingTypeChange] = useState<PendingTypeChange | null>(null);

  const selectedPath = useMemo(() => keyToPath(selectedPathKey), [selectedPathKey]);
  const selectedNode = getNodeAtPath(value, selectedPath) ?? value;
  const treeEntries = useMemo(
    () =>
      buildSchemaTreeEntries(value, {
        rootName: t('campaignPage.callAnalysis.editor.rootNodeLabel'),
        arrayItemName: t('campaignPage.callAnalysis.editor.arrayItemLabel'),
      }),
    [t, value],
  );

  const selectedFieldName = getPathFieldName(selectedPath) ?? '';
  const selectedPathLabel = formatSchemaPath(
    selectedPath,
    t('campaignPage.callAnalysis.editor.rootNodeLabel'),
  );
  const selectedNodeUnsupportedCount = countUnsupportedFields(selectedNode);
  const validationError = validateStructuredOutputSchemaEditorValue(value, error ?? undefined);
  const isRootSelected = selectedPath.length === 0;
  const canEditFieldName = Boolean(getParentPath(selectedPath) && selectedFieldName);
  const canToggleRequired = canEditFieldName && Boolean(getParentPath(selectedPath));
  const isRequired = isRequiredAtPath(value, selectedPath);
  const rootUnsupportedCount = countUnsupportedFields(value);

  useEffect(() => {
    if (!getNodeAtPath(value, selectedPath)) {
      setSelectedPathKey(pathToKey([]));
    }
  }, [selectedPath, value]);

  useEffect(() => {
    setFieldNameDraft(selectedFieldName);
  }, [selectedFieldName, selectedPathKey]);

  useEffect(() => {
    setEnumValueDraft('');
  }, [selectedPathKey]);

  useEffect(() => {
    if (selectedNode.type !== 'string') {
      setIsEnumEditorEnabled(false);
      return;
    }

    setIsEnumEditorEnabled(Boolean(selectedNode.enum?.length));
  }, [selectedNode]);

  const commitFieldName = () => {
    if (!canEditFieldName) {
      return;
    }

    const result = renameObjectPropertyAtPath(value, selectedPath, fieldNameDraft);
    onChange(result.value);
    setSelectedPathKey(pathToKey(result.nextPath));
  };

  const applyNodeUpdate = (
    path: StructuredOutputSchemaPath,
    updater: (node: StructuredOutputSchemaNode) => StructuredOutputSchemaNode,
  ) => {
    onChange(updateNodeAtPath(value, path, updater));
  };

  const applyTypeChange = (
    path: StructuredOutputSchemaPath,
    nextType: StructuredOutputNodeType,
  ) => {
    applyNodeUpdate(path, (node) => changeNodeType(node, nextType));
  };

  const handleTypeChange = (
    path: StructuredOutputSchemaPath,
    nextType: StructuredOutputNodeType | null,
  ) => {
    if (!nextType) {
      return;
    }

    const node = getNodeAtPath(value, path);

    if (!node || node.type === nextType) {
      return;
    }

    if (isDestructiveTypeChange(node, nextType)) {
      setPendingTypeChange({ path, nextType });
      return;
    }

    applyTypeChange(path, nextType);
  };

  const handleAddProperty = (path: StructuredOutputSchemaPath) => {
    const result = addObjectPropertyAtPath(value, path);
    onChange(result.value);
    setSelectedPathKey(pathToKey(result.createdPath));
  };

  const handleAddEnumValue = () => {
    if (selectedNode.type !== 'string') {
      return;
    }

    const trimmedValue = enumValueDraft.trim();

    if (!trimmedValue) {
      return;
    }

    onChange(
      setStringNodeEnumAtPath(value, selectedPath, [...(selectedNode.enum ?? []), trimmedValue]),
    );
    setEnumValueDraft('');
  };

  const handleDeleteEnumValue = (valueToDelete: string, indexToDelete: number) => {
    if (selectedNode.type !== 'string') {
      return;
    }

    onChange(
      setStringNodeEnumAtPath(
        value,
        selectedPath,
        (selectedNode.enum ?? []).filter(
          (enumValue, index) => !(enumValue === valueToDelete && index === indexToDelete),
        ),
      ),
    );
  };

  const renderTypeToggle = () => {
    const typeOptions = isRootSelected ? ROOT_TYPE_OPTIONS : [...ROOT_TYPE_OPTIONS, 'array'];

    return (
      <ToggleButtonGroup
        exclusive
        value={selectedNode.type}
        className={classes.typeToggleGroup}
        onChange={(_, nextType) => handleTypeChange(selectedPath, nextType)}
        disabled={disabled}>
        {typeOptions.map((typeOption) => (
          <ToggleButton key={typeOption} value={typeOption}>
            {t(`campaignPage.callAnalysis.editor.rootTypeOptions.${typeOption}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  return (
    <Box className={classes.root}>
      <Typography variant="body2" color="text.secondary">
        {t('campaignPage.callAnalysis.editor.helperText')}
      </Typography>

      {showValidation && validationError ? (
        <Alert severity="error">
          {t('campaignPage.callAnalysis.editor.emptyObjectError', {
            defaultValue: validationError,
          })}
        </Alert>
      ) : null}

      {rootUnsupportedCount > 0 ? (
        <Alert severity="info">
          {t('campaignPage.callAnalysis.editor.unsupportedNotice', { count: rootUnsupportedCount })}
        </Alert>
      ) : null}

      <Box className={classes.editorSplit}>
        <Paper variant="outlined" className={classes.treePaper}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="subtitle2">
                {t('campaignPage.callAnalysis.editor.variableNameHeader')}
              </Typography>
              <Typography variant="subtitle2">
                {t('campaignPage.callAnalysis.editor.typeHeader')}
              </Typography>
            </Stack>

            <Divider />

            {treeEntries.map((entry) =>
              (() => {
                const isEntryActive = entry.pathKey === selectedPathKey;
                const isEntryRequired =
                  Boolean(getParentPath(entry.path)) && isRequiredAtPath(value, entry.path);
                const canDeleteEntry = Boolean(getParentPath(entry.path)) && !disabled;
                const canAddEntry = entry.node.type === 'object' && !disabled;

                return (
                  <Box
                    key={entry.pathKey}
                    className={cx(
                      classes.treeRow,
                      classes[`treeRowDepth${Math.min(entry.depth, 6)}` as keyof typeof classes],
                      isEntryActive && classes.treeRowActive,
                    )}
                    onClick={() => setSelectedPathKey(entry.pathKey)}
                    aria-label={t('campaignPage.callAnalysis.editor.editFieldDetailsAriaLabel')}>
                    <Box />
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      className={classes.treeRowContent}>
                      <Typography variant="body2" className={classes.treeName}>
                        {entry.name}
                      </Typography>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        className={classes.treeRowMeta}>
                        {isEntryRequired ? (
                          <Chip
                            size="small"
                            label={t('campaignPage.callAnalysis.editor.requiredHeader')}
                            variant="outlined"
                          />
                        ) : null}
                      </Stack>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        className={classes.treeRowActions}>
                        {canAddEntry ? (
                          <IconButton
                            size="small"
                            color="primary"
                            className={classes.treeRowActionButton}
                            aria-label={t('campaignPage.callAnalysis.editor.addPropertyLabel')}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedPathKey(entry.pathKey);
                              handleAddProperty(entry.path);
                            }}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        ) : null}

                        {canDeleteEntry ? (
                          <IconButton
                            size="small"
                            color="error"
                            className={classes.treeRowActionButton}
                            aria-label={t('campaignPage.callAnalysis.editor.deleteFieldLabel')}
                            onClick={(event) => {
                              event.stopPropagation();
                              onChange(deleteObjectPropertyAtPath(value, entry.path));
                              setSelectedPathKey(pathToKey(getParentPath(entry.path) ?? []));
                            }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        ) : null}

                        <Chip
                          size="small"
                          label={entry.node.type}
                          className={classes[badgeClassMap[entry.node.type]]}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                );
              })(),
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" className={classes.fieldPaper}>
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <Typography variant="h6">
                {t('campaignPage.callAnalysis.editor.detailsTitle')}
              </Typography>
              <Typography variant="body2" className={classes.metaLabel}>
                {selectedPathLabel}
              </Typography>
            </Stack>

            {canEditFieldName ? (
              <CustomTextField
                label={t('campaignPage.callAnalysis.editor.propertyNameLabel')}
                value={fieldNameDraft}
                disabled={disabled}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFieldNameDraft(event.target.value)
                }
                onBlur={commitFieldName}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitFieldName();
                  }
                }}
              />
            ) : null}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {isRootSelected
                  ? t('campaignPage.callAnalysis.editor.rootTypeLabel')
                  : t('campaignPage.callAnalysis.editor.fieldTypeLabel')}
              </Typography>
              {renderTypeToggle()}
            </Box>

            <CustomTextField
              label={t('campaignPage.callAnalysis.editor.descriptionLabel')}
              value={selectedNode.description ?? ''}
              disabled={disabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                onChange(setNodeDescriptionAtPath(value, selectedPath, event.target.value))
              }
              placeholder={t('campaignPage.callAnalysis.editor.descriptionPlaceholder')}
              multiline
              minRows={3}
            />

            {selectedNode.type === 'string' ? (
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">
                    {t('campaignPage.callAnalysis.editor.enumValuesLabel')}
                  </Typography>
                  <Switch
                    checked={isEnumEditorEnabled}
                    onChange={(_, checked) => {
                      setIsEnumEditorEnabled(checked);

                      if (!checked) {
                        onChange(setStringNodeEnumAtPath(value, selectedPath, []));
                      }
                    }}
                    disabled={disabled}
                  />
                </Stack>

                {isEnumEditorEnabled ? (
                  <Stack spacing={1.5}>
                    <CustomTextField
                      label={t('campaignPage.callAnalysis.editor.enumValuesLabel')}
                      value={enumValueDraft}
                      disabled={disabled}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setEnumValueDraft(event.target.value)
                      }
                      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleAddEnumValue();
                        }
                      }}
                      placeholder={t('campaignPage.callAnalysis.editor.enumValueInputPlaceholder')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              color="primary"
                              aria-label={t('campaignPage.callAnalysis.editor.addEnumValueLabel')}
                              onClick={handleAddEnumValue}
                              disabled={disabled || !enumValueDraft.trim()}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {(selectedNode.enum ?? []).length ? (
                      <Paper variant="outlined" sx={{ p: 1 }}>
                        <Stack spacing={1}>
                          {(selectedNode.enum ?? []).map((enumValue, index) => (
                            <Stack
                              key={`${enumValue}-${index}`}
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              spacing={1}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {enumValue}
                              </Typography>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={t(
                                  'campaignPage.callAnalysis.editor.deleteEnumValueLabel',
                                )}
                                onClick={() => handleDeleteEnumValue(enumValue, index)}
                                disabled={disabled}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ))}
                        </Stack>
                      </Paper>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            ) : null}

            {canToggleRequired ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle2">
                    {t('campaignPage.callAnalysis.editor.requiredHeader')}
                  </Typography>
                  <Typography variant="body2" className={classes.metaLabel}>
                    {t('campaignPage.callAnalysis.editor.requiredHelper')}
                  </Typography>
                </Box>
                <Switch
                  checked={isRequired}
                  onChange={(_, checked) =>
                    onChange(toggleRequiredAtPath(value, selectedPath, checked))
                  }
                  disabled={disabled}
                />
              </Stack>
            ) : null}

            {selectedNode.type === 'object' && !Object.keys(selectedNode.properties).length ? (
              <Typography variant="body2" className={classes.metaLabel}>
                {t('campaignPage.callAnalysis.editor.emptyStateText')}
              </Typography>
            ) : null}

            {selectedNode.type === 'array' ? (
              <Alert severity="info">
                {t('campaignPage.callAnalysis.editor.arrayItemsHelper')}
              </Alert>
            ) : null}

            {selectedNodeUnsupportedCount > 0 && selectedNode.unsupported ? (
              <Stack spacing={1}>
                <Alert severity="info">
                  {t('campaignPage.callAnalysis.editor.unsupportedNodeNotice', {
                    count: Object.keys(selectedNode.unsupported).length,
                  })}
                </Alert>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                  <pre className={classes.jsonPreview}>
                    {JSON.stringify(selectedNode.unsupported, null, 2)}
                  </pre>
                </Paper>
              </Stack>
            ) : null}
          </Stack>
        </Paper>
      </Box>

      <Dialog
        open={Boolean(pendingTypeChange)}
        onClose={() => setPendingTypeChange(null)}
        fullWidth
        maxWidth="xs">
        <DialogTitle>{t('campaignPage.callAnalysis.editor.confirmTypeChangeTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('campaignPage.callAnalysis.editor.confirmTypeChangeDescription')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingTypeChange(null)}>
            {t('campaignPage.callAnalysis.editor.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingTypeChange) {
                applyTypeChange(pendingTypeChange.path, pendingTypeChange.nextType);
              }
              setPendingTypeChange(null);
            }}>
            {t('campaignPage.callAnalysis.editor.confirmTypeChangeAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StructuredOutputSchemaEditor;
